import React, { useEffect, useState, useRef } from 'react';
import './ErrorAnimation.css';
import errorConfig from '../config/errorConfig';

/**
 * ErrorAnimation Component
 * Displays advanced animated error modal with backdrop, error messages, and action buttons
 * Features: Smooth animations, particle effects, progress bar, and modern design
 * 
 * @param {Object} props
 * @param {Object} props.error - Error object from ErrorContext
 * @param {Function} props.onRetry - Callback function to retry the failed operation
 * @param {Function} props.onDismiss - Callback function to dismiss the error
 */
const ErrorAnimation = ({ error, onRetry, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [progress, setProgress] = useState(100);
  const [particles, setParticles] = useState([]);
  const modalRef = useRef(null);
  const countdownInterval = useRef(null);

  // Generate particles for animation
  useEffect(() => {
    if (error) {
      const newParticles = Array.from({ length: 15 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 8 + 4,
        duration: Math.random() * 3 + 2,
        delay: Math.random() * 0.5
      }));
      setParticles(newParticles);
    }
  }, [error]);

  // Show animation when error appears
  useEffect(() => {
    if (error) {
      setIsVisible(true);
      setIsExiting(false);
      setProgress(100);

      // Start countdown for auto-dismissible errors
      if (error.type === 'client') {
        const startTime = Date.now();
        const endTime = startTime + errorConfig.autoHideDelay;
        const totalDuration = errorConfig.autoHideDelay;

        countdownInterval.current = setInterval(() => {
          const now = Date.now();
          const remaining = Math.ceil((endTime - now) / 1000);
          const elapsed = now - startTime;
          const progressPercent = Math.max(0, 100 - (elapsed / totalDuration) * 100);

          if (remaining > 0) {
            setCountdown(remaining);
            setProgress(progressPercent);
          } else {
            setCountdown(0);
            setProgress(0);
            clearInterval(countdownInterval.current);
          }
        }, 50);
      }

      // Focus on modal for accessibility
      if (modalRef.current) {
        modalRef.current.focus();
      }
    } else {
      // Clear countdown when error is cleared
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
        countdownInterval.current = null;
      }
      setCountdown(null);
      setProgress(100);

      // Trigger exit animation
      setIsExiting(true);
      setTimeout(() => {
        setIsVisible(false);
        setIsExiting(false);
      }, errorConfig.animationDuration);
    }

    // Cleanup on unmount
    return () => {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
    };
  }, [error]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!error) return;

      if (e.key === 'Escape') {
        handleDismiss();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [error]);

  const handleDismiss = () => {
    if (onDismiss) {
      onDismiss();
    }
  };

  const handleRetry = () => {
    // First try the retry callback if available
    if (onRetry) {
      onRetry();
    }

    // Also reload the page to ensure fresh state
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  // Don't render if no error and not visible
  if (!error && !isVisible) {
    return null;
  }

  // Get error icon based on type - Robot style
  const getErrorIcon = (type) => {
    return (
      <div className={`error-robot error-robot-${type}`}>
        {/* Robot Head */}
        <div className="robot-head">
          {/* Antenna */}
          <div className="robot-antenna">
            <div className="robot-antenna-ball"></div>
          </div>

          {/* Eyes - X marks for error */}
          <div className="robot-eyes">
            <div className="robot-eye robot-eye-left">
              <span className="robot-eye-x">×</span>
            </div>
            <div className="robot-eye robot-eye-right">
              <span className="robot-eye-x">×</span>
            </div>
          </div>

          {/* Mouth - sad */}
          <div className="robot-mouth"></div>
        </div>

        {/* Robot Body */}
        <div className="robot-body">
          {/* Error badge on chest */}
          <div className="robot-chest-badge">
            <span className="robot-error-icon">!</span>
          </div>

          {/* Body details */}
          <div className="robot-body-line"></div>
        </div>

        {/* Robot Arms */}
        <div className="robot-arm robot-arm-left"></div>
        <div className="robot-arm robot-arm-right"></div>

        {/* Floating gears */}
        <div className="robot-gear robot-gear-1">⚙</div>
        <div className="robot-gear robot-gear-2">⚙</div>

        {/* Sparks/Error indicators */}
        <div className="robot-spark robot-spark-1">⚡</div>
        <div className="robot-spark robot-spark-2">⚡</div>
      </div>
    );
  };

  return (
    <div
      className={`error-overlay ${isExiting ? 'error-overlay-exit' : ''}`}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="error-title"
      aria-describedby="error-message"
    >
      {/* Animated backdrop with blur */}
      <div
        className="error-backdrop"
        onClick={handleDismiss}
        aria-hidden="true"
      />

      {/* Floating particles background */}
      <div className="error-particles" aria-hidden="true">
        {particles.map(particle => (
          <div
            key={particle.id}
            className="error-particle"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              animationDuration: `${particle.duration}s`,
              animationDelay: `${particle.delay}s`
            }}
          />
        ))}
      </div>

      {/* Main modal */}
      <div
        className={`error-modal error-modal-${error?.type} ${isExiting ? 'error-modal-exit' : ''}`}
        ref={modalRef}
        tabIndex="-1"
      >
        {/* Progress bar for auto-dismiss */}
        {countdown !== null && countdown > 0 && (
          <div className="error-progress-bar" aria-hidden="true">
            <div
              className="error-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {/* Close button */}
        <button
          className="error-close-button"
          onClick={handleDismiss}
          aria-label="Close error dialog"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Creative 404 Oops! Design */}
        <div className="error-creative-title">
          <span className="error-num error-num-one">4</span>
          <span className="error-num error-num-zero">0</span>
          <span className="error-num error-num-oops">Oops!</span>
          <span className="error-num error-num-four">4</span>
        </div>

        {/* Error title with animation */}
        <h2 id="error-title" className="error-title">
          {getErrorTitle(error?.type)}
        </h2>

        {/* Error message */}
        <p id="error-message" className="error-message">
          {error?.message}
        </p>

        {/* Error details badge */}
        {error?.details?.statusCode && (
          <div className="error-badge">
            <span className="error-badge-label">Error Code:</span>
            <span className="error-badge-value">{error.details.statusCode}</span>
          </div>
        )}

        {/* Countdown timer */}
        {countdown !== null && countdown > 0 && (
          <div className="error-countdown" aria-live="polite">
            <svg className="error-countdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span>Auto-closing in {countdown}s</span>
          </div>
        )}

        {/* Action buttons with ripple effect */}
        <div className="error-actions">
          {error?.retryable && (
            <button
              className="error-button error-button-retry"
              onClick={handleRetry}
              aria-label="Retry the failed operation"
            >
              <svg className="error-button-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
              <span>Retry</span>
            </button>
          )}
          <button
            className="error-button error-button-dismiss"
            onClick={handleDismiss}
            aria-label="Dismiss this error"
          >
            <svg className="error-button-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 11 12 14 22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
            <span>Dismiss</span>
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Get user-friendly error title based on error type
 */
const getErrorTitle = (type) => {
  switch (type) {
    case 'network':
      return 'Oops! Lost Connection';
    case 'server':
      return 'Something Went Wrong';
    case 'client':
      return 'Hmm, That Didn\'t Work';
    case 'auth':
      return 'Access Denied';
    default:
      return 'Oops!';
  }
};

export default ErrorAnimation;
